import React, { ReactNode, useCallback, useEffect, useMemo } from "react";
import { ConversationState } from "src/redux/reducers/conversationReducer";
import RecipientHelper from "src/services/recipients.helper";
import {
  Conversation,
  NDAConsent,
  Recipient as RecipientType,
  RecipientInfo,
} from "src/types/conversations";
import dateformat from "dateformat";
import { useDispatch } from "react-redux";
import {
  fetchRecipientInfo,
  toggleVisitorSession,
  updateConversationRecipient,
} from "src/redux/actions/conversation";
import useMeasure from "react-use-measure";
import { toast } from "react-toastify";
import {
  getEmailOpenEvent,
  getViewEvent,
} from "src/services/conversationMetrics.service";

import { DEAL_ROOM_EXPERIENCE } from "src/constants/collections";
import Actions from "src/components/actionbar";
import Tooltip from "src/components/tooltip";
import { Col, Row } from "react-bootstrap";
import { useSpring, animated } from "@react-spring/web";
import NDADetail from "../nda";
import ExcludeSession from "../excludeSession";
import Toast from "src/components/toast";
import { ActivityLoader, RecipientLoader } from "../loaders";

import _ from "lodash";
import cx from "classnames";
import styles from "./recipients.module.scss";
import Contact from "src/components/contact";
import InfiniteScroll from "src/components/infiniteScroll";

interface IRecipientsContainer {
  conversation: ConversationState | object;
  loadMoreRecipientsForConversation: (page: number) => void;
}
interface NDAconsentState {
  show: boolean;
  content: NDAConsent | null;
}
interface ExcludeSessionState {
  show: boolean;
  updating: boolean;
  payload: {
    conversationId: string;
    visitorSessionId: string;
    exclusion: boolean;
    recipientId: string;
  };
}
interface ContactInfo {
  show: boolean;
  contactId: string;
}

const RecipientsContainer: React.FC<IRecipientsContainer> = ({
  conversation,
  loadMoreRecipientsForConversation
}) => {
  //constants
  const dispatch = useDispatch();
  const loadingRecipients = _.get(conversation, "recipients.loading");
  const pagination = _.get(conversation, "recipients.pagination");

  //states
  const [recipients, setRecipients] = React.useState<any>({});
  const [ndaConsent, setNdaConsent] = React.useState<NDAconsentState>({
    show: false,
    content: null,
  });
  const [excludeSession, setExcludeSession] =
    React.useState<ExcludeSessionState>({
      show: false,
      updating: false,
      payload: {
        conversationId: "",
        visitorSessionId: "",
        exclusion: false,
        recipientId: "",
      },
    });
  const [contactInfo, setContactInfo] = React.useState<ContactInfo>({
    show: false,
    contactId: "",
  });

  //effects
  useEffect(() => {
    if (
      _.get(conversation, "recipients.loaded") &&
      _.size(_.get(conversation, "recipients.items")) > 0
    ) {
      const recepientTree = new RecipientHelper(conversation).getRecipients();
      setRecipients(recepientTree);
    }
  }, [_.get(conversation, "recipients")]);

  //functions
  const handleExcludeSession = () => {
    setExcludeSession({
      ...excludeSession,
      updating: true,
    });
    dispatch(
      toggleVisitorSession({
        conversationId: excludeSession.payload.conversationId,
        visitorSessionId: excludeSession.payload.visitorSessionId,
        exclusion: excludeSession.payload.exclusion,
        recipientId: excludeSession.payload.recipientId,
      }),
    );
  };

  const handleCloseExcludeSession = () =>
    setExcludeSession({
      show: false,
      updating: false,
      payload: {
        conversationId: "",
        visitorSessionId: "",
        exclusion: false,
        recipientId: "",
      },
    });

  return (
    <React.Fragment>
      <div className={styles.container}>

        {
          loadingRecipients &&  <RecipientLoader count={4} />
        }

        {
          <InfiniteScroll
            loading={loadingRecipients}
            hasMore={pagination?.hasMoreItems}
            loadMore={() => {
            loadMoreRecipientsForConversation(
              _.get(pagination, "currentPage", 0) + 1,
            );
            }}
            loader={() => {
             return (
              <div className={styles.loading}>loading...</div>
             )
            }}
            className={styles.recipients}
          >
            <Recipients
              recipients={recipients}
              conversation={_.get(conversation, "item") as Conversation}
              setExcludeSession={setExcludeSession}
              setNdaConsent={setNdaConsent}
              setContactInfo={setContactInfo}
            />
          </InfiniteScroll>
        }
        
      </div>
      <NDADetail
        consent={ndaConsent}
        onClose={() => setNdaConsent({ show: false, content: null })}
      />
      <ExcludeSession
        show={excludeSession.show}
        updating={excludeSession.updating}
        onClose={handleCloseExcludeSession}
        onExclude={handleExcludeSession}
      />

      <Contact
        show={contactInfo.show}
        contactId={contactInfo.contactId}
        onClose={() => setContactInfo({ show: false, contactId: "" })}
      />
    </React.Fragment>
  );
};

export default RecipientsContainer;

//components
interface IRecipients {
  recipients: any[];
  conversation: Conversation;
  setExcludeSession: React.Dispatch<React.SetStateAction<ExcludeSessionState>>;
  setNdaConsent: React.Dispatch<React.SetStateAction<NDAconsentState>>;
  setContactInfo: React.Dispatch<React.SetStateAction<ContactInfo>>;
}

const Recipients: React.FC<IRecipients> = ({
  recipients,
  conversation,
  setExcludeSession,
  setNdaConsent,
  setContactInfo,
}) => {
  const renderRecipients = (recipients) => {
    const items: ReactNode[] = [];

    const uniqueRecipients = _.uniqWith(recipients, _.isEqual);

    uniqueRecipients.map((recipient, index) => {
      items.push(
        <Recipient
          conversation={conversation}
          recipient={recipient.item}
          key={_.get(recipient, "item.contact.id", index)}
          setExcludeSession={setExcludeSession}
          showNdaConsent={(content) => setNdaConsent({ show: true, content })}
          setContactInfo={setContactInfo}
        />,
      );

      if (recipient.children != null && recipient.children.length > 0) {
        const children = renderRecipients(recipient.children);

        items.push(<div className={styles.children}>{children}</div>);
      }
    });

    return items;
  };

  return <React.Fragment>{renderRecipients(recipients)}</React.Fragment>;
};
interface IRecipient {
  recipient: RecipientType;
  conversation: Conversation | null;
  // eslint-disable-next-line no-unused-vars
  showNdaConsent: (content: NDAConsent | null) => void;
  // eslint-disable-next-line no-unused-vars
  setExcludeSession: React.Dispatch<React.SetStateAction<ExcludeSessionState>>;
  // eslint-disable-next-line no-unused-vars
  setContactInfo: React.Dispatch<React.SetStateAction<ContactInfo>>;
}

const Recipient: React.FC<IRecipient> = ({
  recipient,
  conversation,
  showNdaConsent,
  setExcludeSession,
  setContactInfo,
}) => {
  //constants
  const contact = _.get(recipient, "contact");
  const visitor = _.get(recipient, "visitor");
  const events = _.get(recipient, "events");
  const deliverStatus: string | null = _.get(recipient, "deliveryStatus", "");
  const dispatch = useDispatch();

  //states
  const [showRecipientInfo, setShowRecipientInfo] = React.useState(false);
  const [exclusionType, setExclusionType] = React.useState("");

  //side effects
  useEffect(() => {
    if (recipient.updatingActivity === false) {
      setExcludeSession({
        show: false,
        updating: false,
        payload: {
          conversationId: "",
          visitorSessionId: "",
          exclusion: false,
          recipientId: "",
        },
      });
      if (exclusionType === "inclusion") {
        toast(<Toast.Sucess message="Visitor included successfully" />);
      } else {
        toast(<Toast.Sucess message="Visitor excluded successfully" />);
      }
    }
  }, [recipient.updatingActivity]);

  //functions
  const recipientEvents = useMemo(() => {
    if (!events) {
      return null;
    }
    const viewEvents = events
      .filter((event) => {
        return (
          event.type === "collection_viewed" ||
          event.type === "asset_viewed" ||
          event.type === "link_clicked" ||
          event.type === "email_open"
        );
      })
      .sort((e1, e2) => {
        return e2.createdDate - e1.createdDate;
      });
    if (viewEvents.length > 0) {
      return viewEvents[0];
    }
  }, [events]);

  const renderName = () => {
    if (contact) {
      return (
        (_.get(contact, "firstName") || "") + (_.get(contact, "lastName") || "")
      );
    } else if (visitor) {
      return (
        (_.get(visitor, "firstName", "") || "") +
        (_.get(visitor, "lastName") || "")
      );
    } else {
      return "";
    }
  };

  const renderAvatar = () => {
    if (visitor) {
      return (
        <div className={styles.avatar}>
          <i className="paperflite-user-settings" />
        </div>
      );
    } else if (contact) {
      if (contact.icon) {
        return <img src={contact.icon} />;
      } else {
        return (
          <div
            className={styles.avatar}
          >
            <span>{(contact.firstName || "")[0]}</span>
            <span>{(contact.lastName || "")[0]}</span>
          </div>
        );
      }
    }
  };

  const renderTimeSpent = (timeSpent) => {
    let timeSpentValue = `~${Math.ceil(timeSpent)}s`;

    if (timeSpent >= 60) {
      timeSpentValue = `~${Math.ceil(timeSpent / 60)}m`;
    }

    return timeSpentValue;
  };

  const emailEvent = useMemo(() => {
    return getEmailOpenEvent(events, _.get(conversation, "source"));
  }, [events]);

  const viewEvent = useMemo(() => {
    return getViewEvent(events);
  }, [events]);

  const reshareMessage = useMemo(() => {
    if (
      !(
        _.get(conversation, "reshares", []) &&
        _.get(conversation, "reshares", []).length != 0
      )
    ) {
      return "";
    }

    const id = _.get(contact, "id");
    if (!id) {
      return "";
    }

    let fromReshare = null;
    let toReshares: any[] = [];
    _.get(conversation, "reshares", []).forEach((reshare) => {
      if (_.get(reshare, "from.id") === id) {
        fromReshare = reshare;
      }

      const toShares = (_.get(reshare, "to", []) as any[]).find((to) => {
        return to.id === id;
      });

      if (toShares) {
        toReshares = [...toReshares, _.get(reshare, "from")];
        _.uniq(toReshares);
      }
    });

    let fromMessage = "";
    if (fromReshare) {
      fromMessage = `Reshared with ${_.get(fromReshare, "to[0].email")} `;
      if (_.size(_.get(fromReshare, "to")) > 1) {
        fromMessage += `and ${_.size(_.get(fromReshare, "to")) - 1} other contact(s)`;
      }
    }

    const toMessage = toReshares.map((to) => {
      return `Reshared by ${to.email} `;
    });

    if (!fromMessage && !toMessage) {
      return "";
    }

    return toMessage + fromMessage;
  }, [conversation]);

  const renderEmailDeliveryStatus = () => {
    switch (deliverStatus) {
      case "soft_bounce":
        return "Bounced";
      case "hard_bounce":
        return "Bounced";
      case "complaint":
        return "Complaint";
      case "not_delivered":
        return "Not Delivered";
      case "did_not_send":
        return "DND recipient not delivered";
      default:
        return "";
    }
  };

  const isDealRoom = () => {
    return _.get(conversation, "viewerType", "") === DEAL_ROOM_EXPERIENCE
      ? true
      : false;
  };

  const toggleSession = (visitorId: string, exclusion: boolean) => {
    if (!exclusion) {
      setExclusionType("inclusion");
      dispatch(
        toggleVisitorSession({
          conversationId: _.get(conversation, "id", ""),
          visitorSessionId: visitorId,
          exclusion,
          recipientId: _.get(contact, "id", "") || _.get(visitor, "id", ""),
        }),
      );
    } else {
      setExclusionType("exclusion");
      setExcludeSession({
        show: true,
        updating: false,
        payload: {
          conversationId: _.get(conversation, "id", ""),
          visitorSessionId: visitorId,
          exclusion,
          recipientId: _.get(contact, "id", "") || _.get(visitor, "id", ""),
        },
      });
    }
  };

  //render
  return (
    <Row className={styles.listItem}>
      <Col
        xl={4}
        xxl={4}
        className={styles.profileInfo}
        onClick={() => {
          if(contact) {
            setContactInfo({ show: true, contactId: contact.id });
          }
        }}
      >
        {renderAvatar()}
        <div>
          <p className={styles.title}>
            <span>{renderName()}</span>
            {_.get(contact, "doNotDisturb") && (
              <span className={styles.dndTag}>DND</span>
            )}
          </p>

          <p className={styles.subTitle}>
            {_.get(contact, "company") && (
              <span className={styles.company}>
                {_.get(contact, "company")}
              </span>
            )}

            <span className={styles.activity}>
              {recipientEvents &&
                `last viewed ${dateformat(recipientEvents.createdDate, "dd mmm yyyy")}`}
              {!recipientEvents && `No activity recorded`}
            </span>
          </p>
        </div>
      </Col>
      <Col xl={8} xxl={8} className={styles.events}>
        {(deliverStatus === "sent" ||
          deliverStatus === "delivered" ||
          !deliverStatus) && (
          <div className={styles.analyticsContainer}>
            {_.get(recipient, "timeSpent", 0) > 0 && (
              <span className={styles.metrics}>
                {renderTimeSpent(recipient.timeSpent)}
              </span>
            )}
            {
              <Tooltip
                content={
                  emailEvent
                    ? `Email Opened on ${dateformat(emailEvent.createdDate, "dd mmm yyyy")}`
                    : ""
                }
                placement="top"
              >
                <span
                  className={cx(styles.metrics, styles.disabled, {
                    [styles.analytics]: emailEvent,
                  })}
                >
                  <i className="paperflite-mail-open" />
                </span>
              </Tooltip>
            }
            {
              <Tooltip
                content={
                  viewEvent
                    ? `last clicked on a link on ${dateformat(viewEvent.createdDate, "dd mmm yyyy")}`
                    : ""
                }
                placement="top"
              >
                <span
                  className={cx(styles.metrics, styles.disabled, {
                    [styles.analytics]: viewEvent,
                  })}
                >
                  <i className="paperflite-click-rate" />
                </span>
              </Tooltip>
            }
            {
              <Tooltip
                content={reshareMessage || "No Reshares"}
                placement="top"
              >
                <span
                  className={cx(styles.metrics, styles.disabled, {
                    [styles.analytics]: reshareMessage,
                  })}
                >
                  <i className="paperflite-share" />
                </span>
              </Tooltip>
            }
            {_.get(recipient, "ndaConsent") && (
              <Tooltip
                content={`NDA signed on ${dateformat(_.get(recipient, "ndaConsentDate"), "dd mmm yyyy")}`}
                placement="top"
              >
                <span
                  className={cx(styles.metrics, styles.analytics)}
                  onClick={() =>
                    showNdaConsent(_.get(recipient, "ndaConsent", null))
                  }
                >
                  <i className="paperflite-nda-consent" />
                </span>
              </Tooltip>
            )}
          </div>
        )}

        {deliverStatus &&
          deliverStatus !== "sent" &&
          deliverStatus !== "delivered" && (
            <span className={styles.deliverStatus}>
              <i className="paperflite-info" />
              {renderEmailDeliveryStatus()}
            </span>
          )}

        {((!isDealRoom() && !visitor && _.get(conversation, "canEdit")) ||
          recipientEvents) && (
          <div className={styles.actionContainer}>
            {!isDealRoom() && !visitor && _.get(conversation, "canEdit") && (
              <Actions.IconButton
                icon={
                  _.get(recipient, "active", false)
                    ? "paperflite-grant-access "
                    : "paperflite-request-access"
                }
                onClick={() => {
                  dispatch(
                    updateConversationRecipient({
                      conversationId: _.get(conversation, "id", ""),
                      recipientId: _.get(contact, "id"),
                      access: _.get(recipient, "active")
                        ? "deactivate"
                        : "activate",
                    }),
                  );
                }}
                disabled={_.get(recipient, "updating", false)}
                tooltip={
                  _.get(recipient, "active", false)
                    ? `${_.get(recipient, "updating") ? "Removing" : "Remove"} access `
                    : `${_.get(recipient, "updating") ? "Granting" : "Grant"} access (Removed on ${recipient.accessRemovedDate ? dateformat(recipient.accessRemovedDate, "dd mmm yyyy") : "N/A"})`
                }
              />
            )}

            {recipientEvents && (
              <Actions.IconButton
                className={cx({
                  [styles.showRecipientInfo]: showRecipientInfo,
                })}
                icon="paperflite-recipient-activity"
                onClick={() => {
                  setShowRecipientInfo(!showRecipientInfo);
                  if (_.size(recipient.activity) === 0) {
                    dispatch(
                      fetchRecipientInfo({
                        recipientId:
                          _.get(contact, "id") || _.get(visitor, "id"),
                        shareId: _.get(conversation, "id", ""),
                      }),
                    );
                  }
                }}
                tooltip="Recipient activity"
              />
            )}
          </div>
        )}
      </Col>
      <ActivityList
        toggleSession={toggleSession}
        show={showRecipientInfo}
        activities={recipient.activity}
        loading={recipient.loadingActivity}
      />
    </Row>
  );
};

interface IActivityList {
  activities: RecipientInfo[];
  show: boolean;
  // eslint-disable-next-line no-unused-vars
  toggleSession: (visitorId: string, exclusion: boolean) => void;
  loading: boolean;
}
const ActivityList: React.FC<IActivityList> = ({
  activities,
  show,
  toggleSession,
  loading
}) => {
  //constants
  const [containerRef, containerBounds] = useMeasure();
  const [childrenAnimation] = useSpring(
    {
      opacity: show ? 1 : 0,
      height: show ? containerBounds.height : 0,
      from: { opacity: 0, height: 0 },
    },
    [show, containerBounds],
  );

  //states
  const [organisedActivity, setOrganisedActivity] =
    React.useState<RecipientInfo[]>(activities);

  //side effects
  useEffect(() => {
    if (_.size(activities) > 3 && activities) {
      setOrganisedActivity(activities.slice(0, 3));
    } else {
      setOrganisedActivity(activities);
    }
  }, [activities]);

  //functions
  const renderSessionAction = (activity: RecipientInfo) => {
    if (
      activity.exclusion &&
      !activity.bot &&
      activity.exclusionType === "MANUAL"
    ) {
      return (
        <span
          onClick={() => {
            toggleSession(activity.visitorId, false);
          }}
        >
          <i
            className={cx("paperflite-include-session", styles.toggleSession)}
          />
          Include Session
        </span>
      );
    } else if (!activity.exclusion) {
      return (
        <span
          onClick={() => {
            toggleSession(activity.visitorId, true);
          }}
        >
          <i
            className={cx("paperflite-exclude-session", styles.toggleSession)}
          />
          Exclude Session
        </span>
      );
    }
  };

  const renderViewMoreButton = useCallback(() => {
    if (_.size(activities) > 3) {
      if (_.size(organisedActivity) < _.size(activities)) {
        return (
          <Row
            className={styles.item}
            onClick={() => setOrganisedActivity(activities)}
          >
            <Col xl={12} className={styles.viewMore}>
              <i className="paperflite-add" />
              {_.size(activities) - 3} More
            </Col>
          </Row>
        );
      } else {
        return (
          <Row
            className={styles.item}
            onClick={() => setOrganisedActivity(activities.slice(0, 3))}
          >
            <Col xl={12} className={styles.viewLess}>
              <i className="paperflite-show-less" />
              show Less
            </Col>
          </Row>
        );
      }
    }
  }, [organisedActivity]);

  //render
  return (
    <animated.div style={childrenAnimation}>
      {show && (
        <div ref={containerRef} className={styles.activityContainer}>
          {
            <>
              {
                loading && <ActivityLoader count={2} />
              } 

              {_.size(organisedActivity) > 0 && (
                <React.Fragment>
                  <Row className={cx(styles.item, styles.header)}>
                    <Col xl={2}>Device</Col>
                    <Col xl={2}>Date & Time</Col>
                    <Col xl={2}>IP Address</Col>
                    <Col xl={3}>Location</Col>
                    <Col xl={2} className={cx(styles.action)}></Col>
                  </Row>
                  {organisedActivity.map((activity, index) => (
                    <Row className={styles.item} key={index}>
                      <Col xl={2}>
                        {activity.browser} on {activity.device}
                      </Col>
                      <Col xl={2}>
                        {dateformat(activity.eventDate, "dd mmm yyyy: HH:MM")}
                      </Col>
                      <Col xl={2}>{activity.ip}</Col>
                      <Col xl={4}>
                        {activity.city}, {activity.state},{" "}
                        {activity.countryCode}
                        {activity.exclusion && (
                          <Tooltip
                            content="This session has been excluded"
                            placement="right"
                          >
                            <i
                              className={cx(
                                "paperflite-info",
                                styles.activityExcluded,
                              )}
                            />
                          </Tooltip>
                        )}
                      </Col>
                      <Col xl={2} className={cx(styles.action)}>
                        {renderSessionAction(activity)}
                      </Col>
                    </Row>
                  ))}

                  {renderViewMoreButton()}
                </React.Fragment>
              )}

              {_.size(organisedActivity) === 0 &&  !loading && (
                <div className={styles.noActivity}>No activity recorded yet, but we&apos;ll be on a look out !</div>
              )}
            </>
          }
        </div>
      )}
    </animated.div>
  );
};
